import { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  UserCog, ArrowLeft, Save, CheckCircle2, AlertTriangle, Camera,
  Shield, MapPin, GitBranch, UserCheck, UserX, Lock, Unlock, KeyRound,
  Eye, EyeOff, ShieldAlert, ShieldCheck, LogIn, AlertCircle,
  Smartphone, X, Hash, User, Mail, Phone, Building2, Tag,
  ChevronRight, ChevronDown, Search,
} from 'lucide-react';
import type { AxiosError } from 'axios';

import { userApi }       from '@/api/user.api';
import { roleApi }       from '@/api/role.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

import type { UserDto, UserStatus, AssignableScopeDto } from '@/types/user.types';
import type { RoleSummaryDto }      from '@/types/role.types';
import {
  USER_STATUS_LABELS,
  USER_STATUS_COLORS,
  USER_TYPE_LABELS,
  avatarColor,
} from '@/types/user.types';

// ── Error extraction ──────────────────────────────────────────────────────────

function extractApiError(err: unknown): string {
  // The API envelope returns errors as [{ field, message }] — earlier this treated them as a
  // Record<string,string[]> and joined the objects, rendering "[object Object]" (QA #76). Read the
  // message off each entry; tolerate the legacy Record shape too.
  const axErr = err as AxiosError<{ message?: string; errors?: unknown }>;
  const data = axErr?.response?.data;
  if (!data) return 'An unexpected error occurred.';
  const e = data.errors;
  let msgs: string[] = [];
  if (Array.isArray(e)) {
    msgs = e.map(x => (typeof x === 'string' ? x : (x as { message?: string })?.message ?? '')).filter(Boolean);
  } else if (e && typeof e === 'object') {
    msgs = Object.values(e as Record<string, unknown>).flat()
      .map(x => (typeof x === 'string' ? x : (x as { message?: string })?.message ?? '')).filter(Boolean);
  }
  if (msgs.length) return msgs.join(' ');
  return data.message ?? 'An unexpected error occurred.';
}

// ── Styling helpers ───────────────────────────────────────────────────────────

const inputCls = (disabled?: boolean) =>
  cn(
    'w-full px-3 py-2 text-sm border rounded-lg transition-colors',
    'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700',
    'text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500',
    disabled && 'bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-70',
  );

const selectCls = (disabled?: boolean) =>
  cn(inputCls(disabled), 'appearance-none');

// ── Field row ─────────────────────────────────────────────────────────────────

interface FieldRowProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}
const FieldRow = ({ label, required, error, hint, children }: FieldRowProps) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-slate-400 dark:text-gray-500">{hint}</p>}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

// ── Section header ────────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title }: { icon: React.ElementType; title: string }) => (
  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100 dark:border-slate-700">
    <Icon size={14} className="text-slate-400" />
    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {title}
    </span>
  </div>
);

// ── Avatar ────────────────────────────────────────────────────────────────────

interface UserAvatarProps {
  name: string;
  imageUrl?: string | null;
  size?: number;
  editable?: boolean;
  uploading?: boolean;
  onFileSelect?: (file: File) => void;
}
const UserAvatar = ({ name, imageUrl, size = 80, editable, uploading, onFileSelect }: UserAvatarProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('');
  const colour = avatarColor(name);

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className="rounded-full object-cover w-full h-full border-2 border-white dark:border-slate-700 shadow"
        />
      ) : (
        <div
          className={cn(
            'rounded-full flex items-center justify-center text-white font-semibold shadow',
            colour,
          )}
          style={{ width: size, height: size, fontSize: size * 0.32 }}
        >
          {initials}
        </div>
      )}

      {editable && (
        <>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 rounded-full flex items-center justify-center
                       bg-black/40 opacity-0 hover:opacity-100 transition-opacity"
            title="Change photo"
          >
            {uploading ? (
              <Spinner size="sm" />
            ) : (
              <Camera size={size * 0.25} className="text-white" />
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) onFileSelect?.(file);
              e.target.value = '';
            }}
          />
        </>
      )}
    </div>
  );
};

// ── Reset Password Modal ──────────────────────────────────────────────────────

interface ResetPasswordModalProps {
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}
const ResetPasswordModal = ({ userId, onClose, onSuccess }: ResetPasswordModalProps) => {
  const [pw,      setPw]      = useState('');
  const [confirm, setConfirm] = useState('');
  const [force,   setForce]   = useState(true);
  const [showPw,  setShowPw]  = useState(false);
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm)  { setError("Passwords don't match.");                  return; }
    setSaving(true);
    setError('');
    try {
      await userApi.resetPassword(userId, { newPassword: pw, forceChangeOnLogin: force });
      onSuccess();
      onClose();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm mx-4">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-blue-600" />
            <span className="font-semibold text-slate-900 dark:text-white text-sm">Reset Password</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs">
              <AlertTriangle size={13} className="shrink-0" />
              {error}
            </div>
          )}

          <FieldRow label="New Password" required>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={pw}
                onChange={e => setPw(e.target.value)}
                autoComplete="new-password"
                className={cn(inputCls(), 'pr-9')}
                placeholder="Min. 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600"
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </FieldRow>

          <FieldRow label="Confirm Password" required>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              className={inputCls()}
            />
          </FieldRow>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={force}
              onChange={e => setForce(e.target.checked)}
              className="rounded border-slate-300 text-blue-600"
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Require password change on next login
            </span>
          </label>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 dark:border-slate-600
                         text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg
                         bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <Spinner size="sm" /> : <KeyRound size={13} />}
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Tabs ──────────────────────────────────────────────────────────────────────

const EDIT_TABS = ['Profile', 'Security', 'Roles', 'Access'] as const;
type Tab = 'Profile' | 'Security' | 'Roles' | 'Access';

// ── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: UserStatus }) => (
  <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', USER_STATUS_COLORS[status])}>
    {USER_STATUS_LABELS[status]}
  </span>
);

// ── Schemas ───────────────────────────────────────────────────────────────────

const profileSchema = z.object({
  firstName:           z.string().min(1, 'Required').max(100),
  lastName:            z.string().min(1, 'Required').max(100),
  email:               z.string().email('Invalid email').max(200),
  phoneNumber:         z.string().max(30).nullable().optional(),
  userCode:            z.string().max(50).nullable().optional(),
  displayName:         z.string().max(100).nullable().optional(),
  employeeRef:         z.string().max(100).nullable().optional(),
  userType:            z.string().nullable().optional(),
  remarks:             z.string().max(500).nullable().optional(),
  defaultLocationId:   z.string().nullable().optional(),
  defaultCostCenterId: z.string().nullable().optional(),
});

const createSchema = profileSchema
  .extend({
    username: z
      .string()
      .min(3, 'Min. 3 characters')
      .max(50)
      .regex(/^[a-zA-Z0-9._-]+$/, 'Letters, digits, dots, hyphens, underscores only'),
    password:        z.string().min(8, 'Min. 8 characters').max(100),
    confirmPassword: z.string(),
  })
  .refine(d => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path:    ['confirmPassword'],
  });

type ProfileValues = z.infer<typeof profileSchema>;
type CreateValues  = z.infer<typeof createSchema>;

// ── UserFormPage ──────────────────────────────────────────────────────────────

export const UserFormPage = () => {
  const { id }            = useParams<{ id: string }>();
  const isNew             = !id;
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canEdit    = hasPermission(Permissions.ADMIN_USER_EDIT);
  const canResetPw = hasPermission(Permissions.ADMIN_USER_RESET_PW);
  const canAssign  = hasPermission(Permissions.ADMIN_USER_EDIT);

  const [activeTab,      setActiveTab]      = useState<Tab>('Profile');
  const [user,           setUser]           = useState<UserDto | null>(null);
  const [allRoles,       setAllRoles]       = useState<RoleSummaryDto[]>([]);
  // One call, already nested: companies > locations > cost centres, plus the cost centres that
  // belong to no location. The ordinary master lists are scoped to the caller's own company, so
  // the access screen could only ever offer the company the administrator happened to be in --
  // which is the opposite of what assigning access is for.
  const [scope, setScope] = useState<AssignableScopeDto>({ companies: [], unassignedCostCentres: [] });
  const [loading,     setLoading]     = useState(!isNew);
  const [saving,      setSaving]      = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [toast,       setToast]       = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Role selection
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [rolesModified,   setRolesModified]   = useState(false);

  // Location access
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  const [defaultLocationId,   setDefaultLocationId]   = useState<string | null>(null);
  const [locationsModified,   setLocationsModified]   = useState(false);

  // Cost centre access
  const [selectedCcIds, setSelectedCcIds] = useState<Set<string>>(new Set());
  const [defaultCcId,   setDefaultCcId]   = useState<string | null>(null);
  const [ccModified,    setCcModified]    = useState(false);

  // Company access
  const [selectedCompanyIds,  setSelectedCompanyIds]  = useState<Set<string>>(new Set());
  const [defaultCompanyId,    setDefaultCompanyId]    = useState<string | null>(null);
  const [companiesModified,   setCompaniesModified]   = useState(false);

  // Access tree: one search across all three levels, and which branches are open.
  const [treeSearch, setTreeSearch] = useState('');
  const [expanded,   setExpanded]   = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) =>
    setExpanded(open => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Profile form (edit mode)
  const profileForm = useForm<ProfileValues>({
    resolver:      zodResolver(profileSchema),
    defaultValues: {
      firstName: '', lastName: '', email: '', phoneNumber: '',
      userCode: '', displayName: '', employeeRef: '', userType: 'Standard',
      remarks: '', defaultLocationId: null, defaultCostCenterId: null,
    },
  });

  // Create form
  const createForm = useForm<CreateValues>({
    resolver:      zodResolver(createSchema),
    defaultValues: {
      username: '', email: '', password: '', confirmPassword: '',
      firstName: '', lastName: '', phoneNumber: '',
      userCode: '', displayName: '', employeeRef: '', userType: 'Standard', remarks: '',
    },
  });

  const [showCreatePw, setShowCreatePw] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load ───────────────────────────────────────────────────────────────────

  const loadUser = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [userRes, rolesRes, scopeRes] = await Promise.all([
        userApi.getById(id),
        roleApi.getList(),
        userApi.assignableScope(),
      ]);
      const u     = userRes.data?.data;
      const roles = rolesRes.data?.data ?? [];
      setAllRoles(roles);
      setScope(scopeRes.data?.data ?? { companies: [], unassignedCostCentres: [] });
      if (u) {
        setUser(u);
        profileForm.reset({
          firstName:           u.firstName,
          lastName:            u.lastName,
          email:               u.email,
          phoneNumber:         u.phoneNumber      ?? '',
          userCode:            u.userCode         ?? '',
          displayName:         u.displayName      ?? '',
          employeeRef:         u.employeeRef      ?? '',
          userType:            u.userType         ?? 'Standard',
          remarks:             u.remarks          ?? '',
          defaultLocationId:   u.defaultLocationId    ?? null,
          defaultCostCenterId: u.defaultCostCenterId  ?? null,
        });
        setSelectedRoleIds(new Set(u.roles.map(r => r.roleId)));
        setSelectedLocationIds(new Set(u.locationAccesses.map(a => a.locationId)));
        setDefaultLocationId(u.locationAccesses.find(a => a.isDefault)?.locationId ?? null);
        setSelectedCcIds(new Set(u.costCenterAccesses.map(a => a.costCenterId)));
        setDefaultCcId(u.costCenterAccesses.find(a => a.isDefault)?.costCenterId ?? null);
        setSelectedCompanyIds(new Set((u.companyAccesses ?? []).map(a => a.companyId)));
        setDefaultCompanyId((u.companyAccesses ?? []).find(a => a.isDefault)?.companyId ?? null);
      }
    } catch {
      showToast('Failed to load user.', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, profileForm]);

  useEffect(() => {
    if (isNew) {
      roleApi.getList().then(r => setAllRoles(r.data?.data ?? []));
    } else {
      loadUser();
    }
  }, [isNew, loadUser]);

  // ── Upload avatar ──────────────────────────────────────────────────────────

  const handleAvatarUpload = async (file: File) => {
    if (!id) return;
    if (file.size > 2 * 1024 * 1024) { showToast('Image must be under 2 MB.', 'error'); return; }
    setUploading(true);
    try {
      await userApi.uploadProfileImage(id, file);
      showToast('Profile photo updated.');
      loadUser();
    } catch (err) {
      showToast(extractApiError(err), 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Save profile ───────────────────────────────────────────────────────────

  const handleSaveProfile = async (values: ProfileValues) => {
    if (!id) return;
    setSaving(true);
    try {
      await userApi.update(id, {
        firstName:           values.firstName,
        lastName:            values.lastName,
        email:               values.email,
        phoneNumber:         values.phoneNumber   || null,
        userCode:            values.userCode      || null,
        displayName:         values.displayName   || null,
        employeeRef:         values.employeeRef   || null,
        userType:            values.userType      || null,
        remarks:             values.remarks       || null,
        defaultLocationId:   values.defaultLocationId   || null,
        defaultCostCenterId: values.defaultCostCenterId || null,
      });
      showToast('Profile saved.');
      loadUser();
    } catch (err) {
      showToast(extractApiError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Create user ────────────────────────────────────────────────────────────

  const handleCreate = async (values: CreateValues) => {
    setSaving(true);
    try {
      const res = await userApi.create({
        username:    values.username,
        email:       values.email,
        password:    values.password,
        firstName:   values.firstName,
        lastName:    values.lastName,
        phoneNumber: values.phoneNumber  || null,
        userCode:    values.userCode     || null,
        displayName: values.displayName  || null,
        employeeRef: values.employeeRef  || null,
        userType:    values.userType     || null,
        remarks:     values.remarks      || null,
        roleIds:     Array.from(selectedRoleIds),
      });
      const newId = res.data?.data?.id;
      showToast('User created successfully.');
      if (newId) setTimeout(() => navigate(`${AppRoutes.ADMIN_USERS}/${newId}`), 1000);
    } catch (err) {
      showToast(extractApiError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Save roles ─────────────────────────────────────────────────────────────

  const handleSaveRoles = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await userApi.assignRoles(id, { roleIds: Array.from(selectedRoleIds) });
      setRolesModified(false);
      showToast('Roles updated.');
      loadUser();
    } catch (err) {
      showToast(extractApiError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Save access ────────────────────────────────────────────────────────────

  const handleSaveAccess = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const ops: Promise<unknown>[] = [];
      if (companiesModified) {
        ops.push(userApi.assignCompanyAccess(id, {
          ids:       Array.from(selectedCompanyIds),
          defaultId: defaultCompanyId,
        }));
      }
      if (locationsModified) {
        ops.push(userApi.assignLocationAccess(id, { ids: Array.from(selectedLocationIds) }));
        if (defaultLocationId !== user?.defaultLocationId) {
          ops.push(userApi.update(id, { defaultLocationId: defaultLocationId }));
        }
      }
      if (ccModified) {
        ops.push(userApi.assignCostCenterAccess(id, { ids: Array.from(selectedCcIds) }));
        if (defaultCcId !== user?.defaultCostCenterId) {
          ops.push(userApi.update(id, { defaultCostCenterId: defaultCcId }));
        }
      }
      await Promise.all(ops);
      setCompaniesModified(false);
      setLocationsModified(false);
      setCcModified(false);
      showToast('Access updated.');
      loadUser();
    } catch (err) {
      showToast(extractApiError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Quick actions ──────────────────────────────────────────────────────────

  const doAction = async (action: () => Promise<unknown>, msg: string) => {
    setSaving(true);
    try {
      await action();
      showToast(msg);
      loadUser();
    } catch (err) {
      showToast(extractApiError(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const formatDate = (d?: string | null) =>
    d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  const activeTabs: Tab[] = isNew ? ['Profile', 'Roles'] : [...EDIT_TABS];
  const tabIcons: Partial<Record<Tab, React.ElementType>> = {
    Security: ShieldAlert,
    Roles:    Shield,
    Access:   MapPin,
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-xl text-sm font-medium flex items-center gap-2',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPw && id && (
        <ResetPasswordModal
          userId={id}
          onClose={() => setShowResetPw(false)}
          onSuccess={() => showToast('Password has been reset.')}
        />
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.ADMIN_USERS)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <UserCog size={20} className="text-blue-600" />
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {isNew ? 'New User' : (user?.fullName ?? 'User')}
                </h1>
                {user && <StatusBadge status={user.status} />}
                {user?.isSystemUser && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full">
                    System
                  </span>
                )}
              </div>
              {user && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  @{user.username}
                  {user.userCode && <span className="ml-2 font-mono">#{user.userCode}</span>}
                  {' · '}{user.email}
                </p>
              )}
            </div>
          </div>

          {/* Quick action buttons */}
          {user && !user.isSystemUser && (canEdit || canResetPw) && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canResetPw && (
                <button
                  onClick={() => setShowResetPw(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                             bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                             hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  <KeyRound size={13} /> Reset Password
                </button>
              )}
              {canEdit && user.status !== 'Locked' && (
                <button
                  onClick={() => doAction(() => userApi.lock(user.id), 'User locked.')}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                             bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400
                             hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                >
                  <Lock size={13} /> Lock
                </button>
              )}
              {canEdit && user.status === 'Locked' && (
                <button
                  onClick={() => doAction(() => userApi.unlock(user.id), 'User unlocked.')}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                             bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400
                             hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                >
                  <Unlock size={13} /> Unlock
                </button>
              )}
              {canEdit && (user.status === 'Active' || user.status === 'PendingActivation') && (
                <button
                  onClick={() => doAction(() => userApi.disable(user.id), 'User disabled.')}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                             bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400
                             hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                >
                  <UserX size={13} /> Disable
                </button>
              )}
              {canEdit && user.status === 'Inactive' && (
                <button
                  onClick={() => doAction(() => userApi.enable(user.id), 'User enabled.')}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                             bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400
                             hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                >
                  <UserCheck size={13} /> Enable
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 -mb-px">
          {activeTabs.map(tab => {
            const Icon = tabIcons[tab];
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300',
                )}
              >
                {Icon && <Icon size={14} />}
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6">

        {/* ================================================================
            PROFILE TAB — Edit mode
            ================================================================ */}
        {activeTab === 'Profile' && !isNew && (
          <form onSubmit={profileForm.handleSubmit(handleSaveProfile)}>
            <div className="max-w-5xl mx-auto flex gap-5">

              {/* Left panel */}
              <div className="w-64 shrink-0 space-y-4">

                {/* Avatar card */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 flex flex-col items-center gap-3">
                  <UserAvatar
                    name={user?.fullName ?? 'U'}
                    imageUrl={user?.profileImageUrl}
                    size={80}
                    editable={canEdit}
                    uploading={uploading}
                    onFileSelect={handleAvatarUpload}
                  />
                  <div className="text-center">
                    <p className="font-semibold text-slate-900 dark:text-white text-sm leading-tight">
                      {user?.displayName || user?.fullName}
                    </p>
                    {user?.displayName && (
                      <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{user.fullName}</p>
                    )}
                    {user && <div className="mt-2"><StatusBadge status={user.status} /></div>}
                  </div>
                  {canEdit && (
                    <p className="text-xs text-slate-400 dark:text-gray-500 text-center">
                      Click photo to change · Max 2 MB
                    </p>
                  )}
                </div>

                {/* Account info */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                  <SectionHeader icon={User} title="Account" />
                  <InfoRow label="Username">
                    <span className="font-mono text-xs">{user?.username}</span>
                  </InfoRow>
                  {user?.userCode && (
                    <InfoRow label="User Code">
                      <span className="font-mono text-xs">#{user.userCode}</span>
                    </InfoRow>
                  )}
                  {user?.userType && (
                    <InfoRow label="Type">
                      <span className="text-xs">{USER_TYPE_LABELS[user.userType] ?? user.userType}</span>
                    </InfoRow>
                  )}
                  {user?.employeeRef && (
                    <InfoRow label="Employee Ref">
                      <span className="text-xs font-mono">{user.employeeRef}</span>
                    </InfoRow>
                  )}
                  <InfoRow label="Created">
                    <span className="text-xs">{formatDate(user?.createdDate)}</span>
                  </InfoRow>
                  {user?.createdByName && (
                    <InfoRow label="Created By">
                      <span className="text-xs">{user.createdByName}</span>
                    </InfoRow>
                  )}
                </div>

                {/* Roles chips */}
                {user && user.roles.length > 0 && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                    <SectionHeader icon={Shield} title="Roles" />
                    <div className="flex flex-wrap gap-1.5">
                      {user.roles.map(r => (
                        <span
                          key={r.roleId}
                          className="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                        >
                          {r.roleName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right panel */}
              <div className="flex-1 space-y-4">

                {/* Identity */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                  <SectionHeader icon={User} title="Identity" />
                  <div className="grid grid-cols-2 gap-4">
                    <FieldRow label="First Name" required error={profileForm.formState.errors.firstName?.message}>
                      <input {...profileForm.register('firstName')} disabled={!canEdit} className={inputCls(!canEdit)} />
                    </FieldRow>
                    <FieldRow label="Last Name" required error={profileForm.formState.errors.lastName?.message}>
                      <input {...profileForm.register('lastName')} disabled={!canEdit} className={inputCls(!canEdit)} />
                    </FieldRow>
                    <FieldRow label="Display Name" error={profileForm.formState.errors.displayName?.message}
                      hint="Shown in greetings and reports">
                      <input {...profileForm.register('displayName')} disabled={!canEdit} className={inputCls(!canEdit)} placeholder={user?.fullName} />
                    </FieldRow>
                    <FieldRow label="User Code" error={profileForm.formState.errors.userCode?.message}
                      hint="Internal employee/user code">
                      <input {...profileForm.register('userCode')} disabled={!canEdit} className={cn(inputCls(!canEdit), 'font-mono')} />
                    </FieldRow>
                    <FieldRow label="Employee Reference" error={profileForm.formState.errors.employeeRef?.message}>
                      <input {...profileForm.register('employeeRef')} disabled={!canEdit} className={cn(inputCls(!canEdit), 'font-mono')} />
                    </FieldRow>
                    <FieldRow label="User Type">
                      <select {...profileForm.register('userType')} disabled={!canEdit} className={selectCls(!canEdit)}>
                        <option value="Standard">Standard User</option>
                        <option value="External">External User</option>
                        <option value="Service">Service Account</option>
                      </select>
                    </FieldRow>
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                  <SectionHeader icon={Mail} title="Contact" />
                  <div className="grid grid-cols-2 gap-4">
                    <FieldRow label="Email" required error={profileForm.formState.errors.email?.message}>
                      <input {...profileForm.register('email')} type="email" disabled={!canEdit} className={inputCls(!canEdit)} />
                    </FieldRow>
                    <FieldRow label="Mobile / Phone" error={profileForm.formState.errors.phoneNumber?.message}>
                      <input {...profileForm.register('phoneNumber')} type="tel" disabled={!canEdit} className={inputCls(!canEdit)} />
                    </FieldRow>
                  </div>
                </div>

                {/* Default context */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                  <SectionHeader icon={Building2} title="Default Context" />
                  <div className="grid grid-cols-2 gap-4">
                    <FieldRow label="Default Location">
                      <select
                        {...profileForm.register('defaultLocationId')}
                        disabled={!canEdit}
                        className={selectCls(!canEdit)}
                      >
                        <option value="">— None —</option>
                        {user?.locationAccesses.map(a => (
                          <option key={a.locationId} value={a.locationId}>
                            {a.locationName} ({a.locationCode})
                          </option>
                        ))}
                      </select>
                    </FieldRow>
                    <FieldRow label="Default Cost Centre">
                      <select
                        {...profileForm.register('defaultCostCenterId')}
                        disabled={!canEdit}
                        className={selectCls(!canEdit)}
                      >
                        <option value="">— None —</option>
                        {user?.costCenterAccesses.map(a => (
                          <option key={a.costCenterId} value={a.costCenterId}>
                            {a.costCenterName} ({a.costCenterCode})
                          </option>
                        ))}
                      </select>
                    </FieldRow>
                  </div>
                  <p className="mt-2 text-xs text-slate-400 dark:text-gray-500">
                    The default location and cost centre are pre-selected when the user starts a new session.
                  </p>
                </div>

                {/* Remarks */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                  <SectionHeader icon={Tag} title="Remarks" />
                  <textarea
                    {...profileForm.register('remarks')}
                    disabled={!canEdit}
                    rows={3}
                    maxLength={500}
                    placeholder="Internal notes or remarks about this user…"
                    className={cn(inputCls(!canEdit), 'resize-none')}
                  />
                  {profileForm.formState.errors.remarks && (
                    <p className="mt-1 text-xs text-red-500">{profileForm.formState.errors.remarks.message}</p>
                  )}
                </div>

                {/* Save button */}
                {canEdit && (
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-blue-600 text-white
                                 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                    >
                      {saving ? <Spinner size="sm" /> : <Save size={15} />}
                      Save Profile
                    </button>
                  </div>
                )}
              </div>
            </div>
          </form>
        )}

        {/* ================================================================
            PROFILE TAB — Create mode
            ================================================================ */}
        {activeTab === 'Profile' && isNew && (
          <form onSubmit={createForm.handleSubmit(handleCreate)}>
            <div className="max-w-3xl mx-auto space-y-4">

              {/* Identity */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <SectionHeader icon={User} title="Identity" />
                <div className="grid grid-cols-2 gap-4">
                  <FieldRow label="First Name" required error={createForm.formState.errors.firstName?.message}>
                    <input {...createForm.register('firstName')} className={inputCls()} />
                  </FieldRow>
                  <FieldRow label="Last Name" required error={createForm.formState.errors.lastName?.message}>
                    <input {...createForm.register('lastName')} className={inputCls()} />
                  </FieldRow>
                  <FieldRow label="Display Name" error={createForm.formState.errors.displayName?.message}
                    hint="Shown in greetings and reports">
                    <input {...createForm.register('displayName')} className={inputCls()} />
                  </FieldRow>
                  <FieldRow label="User Code" error={createForm.formState.errors.userCode?.message}
                    hint="Internal employee/user code">
                    <input {...createForm.register('userCode')} className={cn(inputCls(), 'font-mono')} />
                  </FieldRow>
                  <FieldRow label="Employee Reference" error={createForm.formState.errors.employeeRef?.message}>
                    <input {...createForm.register('employeeRef')} className={cn(inputCls(), 'font-mono')} />
                  </FieldRow>
                  <FieldRow label="User Type">
                    <select {...createForm.register('userType')} className={selectCls()}>
                      <option value="Standard">Standard User</option>
                      <option value="External">External User</option>
                      <option value="Service">Service Account</option>
                    </select>
                  </FieldRow>
                </div>
              </div>

              {/* Login credentials */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <SectionHeader icon={Hash} title="Login Credentials" />
                <div className="grid grid-cols-2 gap-4">
                  <FieldRow label="Username" required error={createForm.formState.errors.username?.message}
                    hint="Letters, digits, dots, hyphens, underscores">
                    <input
                      {...createForm.register('username')}
                      autoComplete="off"
                      className={cn(inputCls(), 'font-mono')}
                    />
                  </FieldRow>
                  <FieldRow label="Email" required error={createForm.formState.errors.email?.message}>
                    <input {...createForm.register('email')} type="email" autoComplete="off" className={inputCls()} />
                  </FieldRow>
                  <FieldRow label="Initial Password" required error={createForm.formState.errors.password?.message}>
                    <div className="relative">
                      <input
                        {...createForm.register('password')}
                        type={showCreatePw ? 'text' : 'password'}
                        autoComplete="new-password"
                        className={cn(inputCls(), 'pr-9')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCreatePw(s => !s)}
                        className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600"
                      >
                        {showCreatePw ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </FieldRow>
                  <FieldRow label="Confirm Password" required error={createForm.formState.errors.confirmPassword?.message}>
                    <input
                      {...createForm.register('confirmPassword')}
                      type={showCreatePw ? 'text' : 'password'}
                      autoComplete="new-password"
                      className={inputCls()}
                    />
                  </FieldRow>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <SectionHeader icon={Phone} title="Contact" />
                <div className="grid grid-cols-2 gap-4">
                  <FieldRow label="Mobile / Phone">
                    <input {...createForm.register('phoneNumber')} type="tel" className={inputCls()} />
                  </FieldRow>
                </div>
              </div>

              {/* Remarks */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <SectionHeader icon={Tag} title="Remarks" />
                <textarea
                  {...createForm.register('remarks')}
                  rows={3}
                  maxLength={500}
                  placeholder="Internal notes or remarks about this user…"
                  className={cn(inputCls(), 'resize-none')}
                />
              </div>

              {/* Create button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-blue-600 text-white
                             rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {saving ? <Spinner size="sm" /> : <Save size={15} />}
                  Create User
                </button>
              </div>
            </div>
          </form>
        )}

        {/* ================================================================
            SECURITY TAB
            ================================================================ */}
        {activeTab === 'Security' && user && (
          <div className="max-w-3xl mx-auto space-y-4">

            {/* Login Activity */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader icon={LogIn} title="Login Activity" />
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <InfoRow label="Last Login">
                  <span className="text-sm">{formatDate(user.lastLoginDate)}</span>
                </InfoRow>
                <InfoRow label="Failed Login Attempts">
                  <span className={cn('text-sm font-semibold', user.failedLoginAttempts > 0 ? 'text-red-500' : 'text-slate-700 dark:text-slate-300')}>
                    {user.failedLoginAttempts}
                  </span>
                </InfoRow>
                {user.lockedUntil && (
                  <InfoRow label="Locked Until">
                    <span className="text-sm text-red-500">{formatDate(user.lockedUntil)}</span>
                  </InfoRow>
                )}
                <InfoRow label="Account Created">
                  <span className="text-sm">{formatDate(user.createdDate)}</span>
                </InfoRow>
              </div>
            </div>

            {/* Password */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader icon={KeyRound} title="Password" />
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <InfoRow label="Last Changed">
                    <span className="text-sm">{formatDate(user.passwordChangedDate)}</span>
                  </InfoRow>
                  <InfoRow label="Force Change on Login">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full',
                      user.forcePasswordChange
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400')}>
                      {user.forcePasswordChange ? 'Yes — change required' : 'No'}
                    </span>
                  </InfoRow>
                </div>
                {canResetPw && !user.isSystemUser && (
                  <button
                    onClick={() => setShowResetPw(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                               bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                               hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    <KeyRound size={13} /> Reset Password
                  </button>
                )}
              </div>
            </div>

            {/* MFA */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <SectionHeader icon={Smartphone} title="Multi-Factor Authentication" />
              <div className="flex items-center gap-3">
                {user.mfaEnabled ? (
                  <ShieldCheck size={20} className="text-green-500" />
                ) : (
                  <AlertCircle size={20} className="text-slate-400" />
                )}
                <div>
                  <p className={cn('text-sm font-medium', user.mfaEnabled ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-gray-400')}>
                    {user.mfaEnabled ? 'MFA is enabled and configured' : 'MFA not configured'}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-gray-500 mt-0.5">
                    {user.mfaEnabled
                      ? 'User authenticates with a second factor on each login.'
                      : 'User has not set up a multi-factor authenticator.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Status */}
            {!user.isSystemUser && canEdit && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <SectionHeader icon={ShieldAlert} title="Account Status" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={user.status} />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {user.status === 'Active'            && 'User can log in normally.'}
                      {user.status === 'Inactive'          && 'User is disabled and cannot log in.'}
                      {user.status === 'Locked'            && 'Account locked due to failed attempts or admin action.'}
                      {user.status === 'PendingActivation' && 'User has not yet activated their account.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.status === 'Locked' && (
                      <button
                        onClick={() => doAction(() => userApi.unlock(user.id), 'User unlocked.')}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                                   bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400
                                   hover:bg-green-200 transition-colors"
                      >
                        <Unlock size={13} /> Unlock
                      </button>
                    )}
                    {(user.status === 'Active' || user.status === 'PendingActivation') && (
                      <button
                        onClick={() => doAction(() => userApi.disable(user.id), 'User disabled.')}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                                   bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400
                                   hover:bg-amber-200 transition-colors"
                      >
                        <UserX size={13} /> Disable
                      </button>
                    )}
                    {user.status === 'Inactive' && (
                      <button
                        onClick={() => doAction(() => userApi.enable(user.id), 'User enabled.')}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                                   bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400
                                   hover:bg-green-200 transition-colors"
                      >
                        <UserCheck size={13} /> Enable
                      </button>
                    )}
                    {user.status !== 'Locked' && (
                      <button
                        onClick={() => doAction(() => userApi.lock(user.id), 'User locked.')}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                                   bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400
                                   hover:bg-red-200 transition-colors"
                      >
                        <Lock size={13} /> Lock
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================
            ROLES TAB
            ================================================================ */}
        {activeTab === 'Roles' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900 dark:text-white">Role Assignment</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Select the roles to assign to this user.
                  </p>
                </div>
                {canAssign && rolesModified && !isNew && (
                  <button
                    onClick={handleSaveRoles}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white
                               rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? <Spinner size="sm" /> : <Save size={14} />}
                    Save Roles
                  </button>
                )}
              </div>

              {allRoles.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-gray-500 italic">No roles available.</p>
              ) : (
                <div className="space-y-2">
                  {allRoles.map(role => (
                    <label
                      key={role.id}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        selectedRoleIds.has(role.id)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
                        !canAssign && 'cursor-not-allowed opacity-60',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoleIds.has(role.id)}
                        disabled={!canAssign}
                        onChange={e => {
                          const next = new Set(selectedRoleIds);
                          if (e.target.checked) next.add(role.id);
                          else                  next.delete(role.id);
                          setSelectedRoleIds(next);
                          if (!isNew) setRolesModified(true);
                        }}
                        className="mt-0.5 rounded border-slate-300 text-blue-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-900 dark:text-white">{role.name}</span>
                          {role.isSystemRole && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                              System
                            </span>
                          )}
                          {!role.isActive && (
                            <span className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs font-mono text-slate-500 dark:text-gray-400">{role.code}</span>
                          {role.description && (
                            <span className="text-xs text-slate-400 dark:text-gray-500">· {role.description}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          {role.permissionCount} permission{role.permissionCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================================================================
            ACCESS TAB
            ================================================================ */}
        {activeTab === 'Access' && user && (
          <div className="max-w-6xl mx-auto space-y-4">

            {/* Header bar */}
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Assign which companies, locations, and cost centres this user can access.
                These selections control what's available at login and in the context switcher.
              </p>
              {canAssign && (companiesModified || locationsModified || ccModified) && (
                <button
                  onClick={handleSaveAccess}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-blue-600 text-white
                             rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm shrink-0"
                >
                  {saving ? <Spinner size="sm" /> : <Save size={15} />}
                  Save access
                </button>
              )}
            </div>

            {/* -- Access tree ---------------------------------------------
                Company > Location > Cost centre, nested so it is obvious which
                belongs to which. Three flat lists could not show that at all.

                Each checkbox is that record's OWN grant and nothing cascades.
                Access is three independent lists (CLAUDE.md 7.2 checks company,
                then location, then cost centre), so ticking a company must not
                silently tick its locations: leaving a level empty means "not
                narrowed", and filling it in would freeze the set so a location
                added next year would not be included. What the tree adds is
                visibility -- including when a child is granted under a parent
                that is not, which is a grant that can never take effect. */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <Search size={14} className="text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={treeSearch}
                  onChange={e => setTreeSearch(e.target.value)}
                  placeholder="Search companies, locations or cost centres..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
                <span className="text-xs text-slate-400 shrink-0">
                  {selectedCompanyIds.size} co | {selectedLocationIds.size} loc | {selectedCcIds.size} cc
                </span>
              </div>

              <div data-testid="access-tree" className="max-h-[28rem] overflow-y-auto py-1">
                {scope.companies.map(co => {
                  const locs = co.locations;
                  const ccsOf = (locId: string) =>
                    locs.find(l => l.id === locId)?.costCentres ?? [];

                  const hit = (t: string) => t.toLowerCase().includes(treeSearch.toLowerCase());
                  const matches = !treeSearch
                    || hit(co.name) || hit(co.code)
                    || locs.some(l => hit(l.name) || hit(l.code)
                         || ccsOf(l.id).some(c => hit(c.name) || hit(c.code)));
                  if (!matches) return null;

                  const coChecked = selectedCompanyIds.has(co.id);
                  const isOpen = expanded.has(co.id) || !!treeSearch;
                  const grantedLocs = locs.filter(l => selectedLocationIds.has(l.id)).length;
                  // A grant beneath a company the user cannot enter never takes effect.
                  const orphaned = !coChecked && grantedLocs > 0;

                  return (
                    <div key={co.id}>
                      <div className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg mx-1 group',
                        coChecked ? 'bg-blue-50 dark:bg-blue-900/15' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                      )}>
                        <button type="button" onClick={() => toggleExpanded(co.id)}
                          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${co.code}`}
                          className="p-0.5 text-slate-400 hover:text-slate-600 shrink-0"
                          disabled={locs.length === 0}
                        >
                          {locs.length === 0
                            ? <span className="inline-block w-[14px]" />
                            : isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>

                        <input
                          type="checkbox"
                          checked={coChecked}
                          disabled={!canAssign}
                          aria-label={`Company ${co.code}`}
                          onChange={e => {
                            const next = new Set(selectedCompanyIds);
                            if (e.target.checked) next.add(co.id);
                            else {
                              next.delete(co.id);
                              if (defaultCompanyId === co.id) setDefaultCompanyId(null);
                            }
                            setSelectedCompanyIds(next);
                            setCompaniesModified(true);
                          }}
                          className="rounded border-slate-300 text-blue-600 shrink-0"
                        />

                        <Building2 size={14} className="text-blue-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate block">{co.name}</span>
                          <span className="text-xs text-slate-400 font-mono">{co.code}</span>
                        </div>

                        {locs.length > 0 && (
                          <span className="text-xs text-slate-400 shrink-0">{grantedLocs}/{locs.length} loc</span>
                        )}

                        {orphaned && (
                          <span title="Locations are granted under a company this user cannot enter, so they have no effect until the company is granted."
                            className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
                            no company access
                          </span>
                        )}

                        {coChecked && canAssign && (
                          <button type="button"
                            onClick={() => { setDefaultCompanyId(defaultCompanyId === co.id ? null : co.id); setCompaniesModified(true); }}
                            className={cn('text-xs px-2 py-0.5 rounded-full shrink-0 transition-colors',
                              defaultCompanyId === co.id
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500 opacity-0 group-hover:opacity-100')}
                          >
                            {defaultCompanyId === co.id ? 'Default' : 'Set default'}
                          </button>
                        )}
                      </div>

                      {isOpen && locs.map(loc => {
                        const ccs = ccsOf(loc.id);
                        const locChecked = selectedLocationIds.has(loc.id);
                        const locOpen = expanded.has(loc.id) || !!treeSearch;
                        const grantedCcs = ccs.filter(c => selectedCcIds.has(c.id)).length;
                        const ccOrphaned = !locChecked && grantedCcs > 0;

                        return (
                          <div key={loc.id}>
                            <div className={cn(
                              'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-lg mx-1 group',
                              locChecked ? 'bg-blue-50/60 dark:bg-blue-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                            )}>
                              <button type="button" onClick={() => toggleExpanded(loc.id)}
                                aria-label={`${locOpen ? 'Collapse' : 'Expand'} ${loc.code}`}
                                className="p-0.5 text-slate-400 hover:text-slate-600 shrink-0"
                                disabled={ccs.length === 0}
                              >
                                {ccs.length === 0
                                  ? <span className="inline-block w-[14px]" />
                                  : locOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>

                              <input
                                type="checkbox"
                                checked={locChecked}
                                disabled={!canAssign}
                                aria-label={`Location ${loc.code}`}
                                onChange={e => {
                                  const next = new Set(selectedLocationIds);
                                  if (e.target.checked) next.add(loc.id);
                                  else {
                                    next.delete(loc.id);
                                    if (defaultLocationId === loc.id) setDefaultLocationId(null);
                                  }
                                  setSelectedLocationIds(next);
                                  setLocationsModified(true);
                                }}
                                className="rounded border-slate-300 text-blue-600 shrink-0"
                              />

                              <MapPin size={13} className="text-emerald-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm text-slate-700 dark:text-slate-300 truncate block">{loc.name}</span>
                                <span className="text-xs text-slate-400 font-mono">{loc.code}</span>
                              </div>

                              {ccs.length > 0 && (
                                <span className="text-xs text-slate-400 shrink-0">{grantedCcs}/{ccs.length} cc</span>
                              )}

                              {ccOrphaned && (
                                <span title="Cost centres are granted under a location this user cannot enter."
                                  className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
                                  no location access
                                </span>
                              )}

                              {locChecked && canAssign && (
                                <button type="button"
                                  onClick={() => { setDefaultLocationId(defaultLocationId === loc.id ? null : loc.id); setLocationsModified(true); }}
                                  className={cn('text-xs px-2 py-0.5 rounded-full shrink-0 transition-colors',
                                    defaultLocationId === loc.id
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                      : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500 opacity-0 group-hover:opacity-100')}
                                >
                                  {defaultLocationId === loc.id ? 'Default' : 'Set default'}
                                </button>
                              )}
                            </div>

                            {locOpen && ccs.map(cc => {
                              const ccChecked = selectedCcIds.has(cc.id);
                              return (
                                <div key={cc.id} className={cn(
                                  'flex items-center gap-2 pl-[4.25rem] pr-3 py-1.5 rounded-lg mx-1 group',
                                  ccChecked ? 'bg-blue-50/40 dark:bg-blue-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                                )}>
                                  <input
                                    type="checkbox"
                                    checked={ccChecked}
                                    disabled={!canAssign}
                                    aria-label={`Cost centre ${cc.code}`}
                                    onChange={e => {
                                      const next = new Set(selectedCcIds);
                                      if (e.target.checked) next.add(cc.id);
                                      else {
                                        next.delete(cc.id);
                                        if (defaultCcId === cc.id) setDefaultCcId(null);
                                      }
                                      setSelectedCcIds(next);
                                      setCcModified(true);
                                    }}
                                    className="rounded border-slate-300 text-blue-600 shrink-0"
                                  />
                                  <GitBranch size={13} className="text-violet-500 shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-slate-600 dark:text-slate-400 truncate block">{cc.name}</span>
                                    <span className="text-xs text-slate-400 font-mono">{cc.code}</span>
                                  </div>
                                  {ccChecked && canAssign && (
                                    <button type="button"
                                      onClick={() => { setDefaultCcId(defaultCcId === cc.id ? null : cc.id); setCcModified(true); }}
                                      className={cn('text-xs px-2 py-0.5 rounded-full shrink-0 transition-colors',
                                        defaultCcId === cc.id
                                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                          : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500 opacity-0 group-hover:opacity-100')}
                                    >
                                      {defaultCcId === cc.id ? 'Default' : 'Set default'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}

                            {locOpen && ccs.length === 0 && (
                              <p className="pl-[4.25rem] pr-3 py-1.5 text-xs text-slate-400 italic">
                                No cost centres under this location.
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {isOpen && locs.length === 0 && (
                        <p className="pl-9 pr-3 py-1.5 text-xs text-slate-400 italic">
                          No locations under this company.
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Cost centres belonging to no location.
                    They hang off nothing, so the tree would drop them silently -- which is the
                    same disappearing act that made an untagged cost centre useless in the first
                    place. Shown here so they can still be granted, and so it is obvious they need
                    a location before any voucher will offer them. */}
                {(() => {
                  const orphanCcs = scope.unassignedCostCentres;
                  if (orphanCcs.length === 0) return null;

                  return (
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                      <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                        Cost centres with no location ({orphanCcs.length})
                      </p>
                      <p className="px-3 pb-1 text-xs text-slate-400">
                        These are offered on no voucher until a location is set on the Cost Centre screen.
                      </p>
                      {orphanCcs.map(cc => {
                        const ccChecked = selectedCcIds.has(cc.id);
                        return (
                          <div key={cc.id} className={cn(
                            'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-lg mx-1',
                            ccChecked ? 'bg-blue-50/40 dark:bg-blue-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                          )}>
                            <input
                              type="checkbox"
                              checked={ccChecked}
                              disabled={!canAssign}
                              aria-label={`Cost centre ${cc.code}`}
                              onChange={e => {
                                const next = new Set(selectedCcIds);
                                if (e.target.checked) next.add(cc.id);
                                else {
                                  next.delete(cc.id);
                                  if (defaultCcId === cc.id) setDefaultCcId(null);
                                }
                                setSelectedCcIds(next);
                                setCcModified(true);
                              }}
                              className="rounded border-slate-300 text-blue-600 shrink-0"
                            />
                            <GitBranch size={13} className="text-amber-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-slate-600 dark:text-slate-400 truncate block">{cc.name}</span>
                              <span className="text-xs text-slate-400 font-mono">{cc.code}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {scope.companies.length === 0 && (
                  <p className="text-center text-sm text-slate-400 py-8">No companies found.</p>
                )}
              </div>
            </div>

            {/* Grants that can never take effect.
                A location row already carries a "no location access" chip, but it sits inside the
                tree and is only visible once somebody expands that branch — so the one warning
                that matters most is the easiest to miss. Counted here across every company, where
                it cannot be collapsed out of sight.

                Only meaningful once locations have been narrowed at all: with none selected every
                location is reachable, so no cost centre under one can be stranded. */}
            {(() => {
              if (selectedLocationIds.size === 0) return null;

              const stranded = scope.companies.flatMap(co =>
                co.locations.flatMap(loc =>
                  selectedLocationIds.has(loc.id)
                    ? []
                    : (loc.costCentres ?? []).filter(cc => selectedCcIds.has(cc.id))));

              if (stranded.length === 0) return null;

              return (
                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    {stranded.length === 1
                      ? `Cost centre ${stranded[0].code} is granted under a location this user cannot enter, so the grant cannot take effect.`
                      : `${stranded.length} cost centres (${stranded.slice(0, 4).map(c => c.code).join(', ')}${stranded.length > 4 ? ', …' : ''}) are granted under locations this user cannot enter, so those grants cannot take effect.`}
                    {' '}Tick the location as well, or untick the cost centre.
                  </span>
                </div>
              );
            })()}

            {/* An empty level means "not narrowed", not "denied" -- stated per level, because a
                tree cannot show the absence of a restriction. */}
            <div className="grid grid-cols-3 gap-3">
              {([
                ['company', 'companies', selectedCompanyIds.size, 'the user falls back to their own company'],
                ['location', 'locations', selectedLocationIds.size, 'all locations are available'],
                ['cost centre', 'cost centres', selectedCcIds.size, 'all cost centres are available'],
              ] as const).map(([one, many, count, unrestricted]) => (
                <p key={many} className={cn(
                  'text-xs rounded-lg px-3 py-2',
                  count === 0
                    ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/15'
                    : 'text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/40',
                )}>
                  {count === 0
                    ? `No ${many} selected, so ${unrestricted}.`
                    : `${count} ${count === 1 ? one : many} selected.`}
                </p>
              ))}
            </div>


            <div className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                The marked default is what pre-selects when the user signs in. Ticking a company
                does not tick its locations: each level is granted on its own, and a level left
                empty is not narrowed rather than denied. Platform admins (SUPER_ADMIN) bypass
                company restrictions regardless of this list.
              </span>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

// ── Info Row helper ───────────────────────────────────────────────────────────

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-1">
    <span className="text-xs text-slate-500 dark:text-slate-400 w-28 shrink-0 pt-0.5">{label}</span>
    <span className="text-sm text-slate-800 dark:text-slate-200 font-medium">{children}</span>
  </div>
);
